import { useMemo, useState } from "react";
import { Link } from "react-router";
import type { BuildingSummary } from "@klantpunt/shared";
import { BaseMap, type MapPin } from "./BaseMap";

/** Alle gebouwen op de kaart; een pin aantikken toont een kaartje met de open punten. */
export function BuildingsMap({ buildings, canManage }: { buildings: BuildingSummary[]; canManage: boolean }) {
  const [sel, setSel] = useState<string | null>(null);
  const located = buildings.filter((b) => b.lat != null && b.lng != null);
  const missing = buildings.filter((b) => b.lat == null || b.lng == null);
  const pins = useMemo<MapPin[]>(
    () =>
      located.map((b) => ({
        id: b.id,
        lat: b.lat!,
        lng: b.lng!,
        label: b.code,
        title: `${b.code} ${b.name}`,
        tone: (b.urgentCount ?? 0) > 0 ? "urgent" : "default",
        selected: b.id === sel,
      })),
    [located, sel],
  );
  const cur = buildings.find((b) => b.id === sel);
  return (
    <div className="mapview">
      <div className="mapwrap">
        <BaseMap
          className="overviewmap"
          label="Kaart met gebouwen"
          pins={pins}
          onPinClick={setSel}
          onPick={() => setSel(null)}
          fitKey={located.map((b) => b.id).join(",")}
        />
        {cur && (
          <div className="mapcard" role="dialog" aria-label={`${cur.code} ${cur.name}`}>
            <div className="row">
              <span className="code">{cur.code}</span>
              <strong className="grow">{cur.name}</strong>
              <button type="button" className="btn small ghost" onClick={() => setSel(null)} aria-label="Sluiten">
                ×
              </button>
            </div>
            {cur.address && <p className="muted small">{cur.address}</p>}
            <p className="small">
              {cur.openCount ? (
                <>
                  <strong className="num">{cur.openCount}</strong> open {cur.openCount === 1 ? "punt" : "punten"}
                  {cur.urgentCount ? <span className="chip prio prio-P1">{cur.urgentCount} spoed/hoog</span> : null}
                </>
              ) : (
                <span className="muted">Geen open punten</span>
              )}
            </p>
            <Link className="btn primary small" to={`/gebouwen/${cur.id}`}>
              Openen
            </Link>
          </div>
        )}
      </div>
      {missing.length > 0 && (
        <p className="hint">
          Zonder locatie: {missing.map((b) => b.code).join(", ")}.
          {canManage && " Open het gebouw en vul onder Gebouw → Gegevens bewerken het adres in."}
        </p>
      )}
    </div>
  );
}
